import { useMemo, useState, type ReactNode } from 'react';
import { bulkEdit, type BulkChanges } from '../../model/bulk';
import { findEvent, genderLabel, latestEvent, primaryName, shortName } from '../../model/people';
import type { GenderIdentity, Id, Person, TermStyle } from '../../model/types';
import { plural, TERM_OPTIONS } from '../labels';
import { closeDialog, commit, getState, toast, undo, useApp } from '../store';
import { Avatar, Modal } from './common';
import { GenderPicker } from './GenderPicker';
import { PlaceInput } from './PlaceInput';
import { PronounPicker } from './PronounPicker';

type DetailKey = 'living' | 'gender' | 'pronouns' | 'terms' | 'surname' | 'birthplace' | 'residence' | 'occupation';
const TEXT_FIELDS: { key: 'surname' | 'birthplace' | 'residence' | 'occupation'; label: string; get: (p: Person) => string; hint?: string; place?: boolean }[] = [
  { key: 'surname', label: 'Last name', get: (p) => primaryName(p)?.surname ?? '', hint: 'On the name each person goes by; their other names stay as they are.' },
  { key: 'birthplace', label: 'Birthplace', get: (p) => findEvent(p, 'birth')?.place ?? '', place: true },
  { key: 'residence', label: 'Lives in', get: (p) => latestEvent(p, 'residence')?.place ?? '', hint: 'Changes each person’s most recent home, or adds one.', place: true },
  { key: 'occupation', label: 'Occupation', get: (p) => latestEvent(p, 'occupation')?.label ?? '', hint: 'Changes each person’s most recent job, or adds one.' },
];
const LIVING_OPTIONS = [
  { value: 'living', label: 'Living' },
  { value: 'deceased', label: 'Deceased' },
  { value: 'unknown', label: 'Not recorded' },
] as const;
const livingValue = (living: boolean | null | undefined) => (living === null ? 'unknown' : living ? 'living' : 'deceased');

/** What everyone has in common: the shared value, or `mixed` when it differs. */
function shared<T>(people: Person[], get: (p: Person) => T): { value: T; mixed: false } | { value: undefined; mixed: true } {
  const first = get(people[0]);
  return people.every((p) => get(p) === first) ? { value: first, mixed: false } : { value: undefined, mixed: true };
}

/** Change details, tags, groups, and notes for several people at once. Fields left unticked stay as they are. */
export function BulkEditDialog({ personIds }: { personIds: Id[] }) {
  const tree = useApp((s) => s.tree)!;
  const people = useMemo(() => personIds.map((id) => tree.people[id]).filter(Boolean), [tree, personIds]);
  const [details, setDetails] = useState<Pick<BulkChanges, DetailKey>>({});
  const [addTags, setAddTags] = useState('');
  const [removeTags, setRemoveTags] = useState<string[]>([]);
  const [joinGroup, setJoinGroup] = useState('');
  const [role, setRole] = useState('');
  const [leaveGroup, setLeaveGroup] = useState('');
  const [notes, setNotes] = useState('');

  const tagCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const p of people) for (const t of p.tags) counts.set(t, (counts.get(t) ?? 0) + 1);
    return [...counts].sort((a, b) => a[0].localeCompare(b[0]));
  }, [people]);
  const groups = useMemo(() => Object.values(tree.groups).sort((a, b) => a.name.localeCompare(b.name)), [tree]);
  const ids = new Set(people.map((p) => p.id));
  const inGroup = (gid: string) => tree.groups[gid]?.members.filter((m) => ids.has(m.personId)).length ?? 0;

  if (!people.length) return null;

  const on = (k: DetailKey) => k in details;
  const set = <K extends DetailKey>(k: K, v: BulkChanges[K]) => setDetails((d) => ({ ...d, [k]: v }));
  const toggle = (k: DetailKey, start: () => BulkChanges[DetailKey]) =>
    setDetails((d) => {
      if (k in d) {
        const { [k]: _, ...rest } = d;
        return rest;
      }
      return { ...d, [k]: start() };
    });

  const living = shared(people, (p) => livingValue(p.living));
  const gender = shared(people, (p) => (p.gender.identity === 'custom' ? `custom:${p.gender.label ?? ''}` : p.gender.identity));
  const pronouns = shared(people, (p) => p.pronouns ?? '');
  const terms = shared(people, (p) => p.terms ?? 'auto');

  const changes: BulkChanges = {
    ...details,
    addTags: addTags.split(',').map((t) => t.trim()).filter(Boolean),
    removeTags,
    addToGroup: joinGroup ? { groupId: joinGroup, role } : undefined,
    removeFromGroup: leaveGroup || undefined,
    appendNotes: notes.trim() || undefined,
  };
  const anything = Object.keys(details).length > 0 || !!changes.addTags!.length || removeTags.length > 0 || !!joinGroup || !!leaveGroup || !!changes.appendNotes;

  const apply = () => {
    const { tree: next, changed } = bulkEdit(getState().tree!, people.map((p) => p.id), changes);
    closeDialog();
    if (!changed) return toast('Nothing to change: they already have those details.');
    commit(next);
    toast(`Updated ${plural(changed, 'person', 'people')}.`, 'success', { label: 'Undo', run: undo });
  };

  const shownNames = people.slice(0, 3).map((p) => shortName(p));
  const more = people.length - shownNames.length;

  return (
    <Modal
      title={`Edit ${plural(people.length, 'person', 'people')}`}
      wide
      footer={
        <>
          <button className="btn" onClick={closeDialog}>
            Cancel
          </button>
          <button className="btn primary" disabled={!anything} onClick={apply}>
            Apply to {plural(people.length, 'person', 'people')}
          </button>
        </>
      }
    >
      <div className="stack">
        <div className="row bulk-who">
          <span className="avatar-stack" aria-hidden>
            {people.slice(0, 5).map((p) => (
              <Avatar key={p.id} person={p} size="sm" />
            ))}
          </span>
          <span className="muted small-text">
            {shownNames.join(', ')}
            {more > 0 ? `, and ${plural(more, 'other')}` : ''}
          </span>
        </div>
        <p className="faint small-text">Tick what to change. Everything else stays as it is for each person.</p>

        <div className="section-title">Details</div>
        <div className="bulk-grid">
          <BulkRow autoFocus label="Living status" on={on('living')} onToggle={() => toggle('living', () => (living.mixed ? null : living.value === 'unknown' ? null : living.value === 'living'))}>
            <select
              className="select"
              aria-label="Living status"
              value={on('living') ? livingValue(details.living) : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (!v) return toggle('living', () => null);
                set('living', v === 'unknown' ? null : v === 'living');
              }}
            >
              {!on('living') && <option value="">{living.mixed ? 'Mixed' : LIVING_OPTIONS.find((o) => o.value === living.value)!.label}</option>}
              {LIVING_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </BulkRow>
          <BulkRow label="Gender" on={on('gender')} onToggle={() => toggle('gender', () => (gender.mixed ? { identity: 'unknown' } : { ...people[0].gender }))} hint={on('gender') && !on('pronouns') ? 'People without pronouns get the usual ones for this gender.' : undefined}>
            <div className="row" style={{ gap: 6 }}>
              <GenderPicker
                ariaLabel="Gender"
                value={details.gender?.identity ?? (gender.mixed ? 'unknown' : people[0].gender.identity)}
                placeholder={on('gender') ? undefined : gender.mixed ? 'Mixed' : genderLabel(people[0].gender)}
                onChange={(identity: GenderIdentity) => set('gender', { identity, label: identity === 'custom' ? details.gender?.label ?? '' : undefined })}
              />
              {details.gender?.identity === 'custom' && (
                <input className="input" aria-label="Gender, in their words" placeholder="In their words" value={details.gender.label ?? ''} onChange={(e) => set('gender', { identity: 'custom', label: e.target.value })} />
              )}
            </div>
          </BulkRow>
          <BulkRow label="Pronouns" on={on('pronouns')} onToggle={() => toggle('pronouns', () => pronouns.value ?? '')}>
            <PronounPicker ariaLabel="Pronouns" value={on('pronouns') ? details.pronouns || undefined : pronouns.value || undefined} placeholder={on('pronouns') ? undefined : pronouns.mixed ? 'Mixed' : undefined} onChange={(v) => set('pronouns', v ?? '')} />
          </BulkRow>
          <BulkRow label="Relationship words" on={on('terms')} onToggle={() => toggle('terms', () => terms.value ?? 'auto')}>
            <select
              className="select"
              aria-label="Relationship words"
              value={on('terms') ? details.terms : ''}
              onChange={(e) => (e.target.value ? set('terms', e.target.value as TermStyle) : toggle('terms', () => 'auto'))}
            >
              {!on('terms') && <option value="">{terms.mixed ? 'Mixed' : TERM_OPTIONS.find((o) => o.value === terms.value)!.label}</option>}
              {TERM_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </BulkRow>
          {TEXT_FIELDS.map((f) => {
            const now = shared(people, f.get);
            const input = {
              className: 'input',
              'aria-label': f.label,
              value: on(f.key) ? details[f.key] ?? '' : '',
              placeholder: now.mixed ? 'Mixed' : now.value || 'Not recorded',
            };
            return (
              <BulkRow key={f.key} label={f.label} on={on(f.key)} onToggle={() => toggle(f.key, () => now.value ?? '')} hint={on(f.key) ? (details[f.key] ? f.hint : `Leave empty to clear ${f.label.toLowerCase()} for everyone.`) : undefined}>
                {f.place ? <PlaceInput {...input} onChange={(v) => set(f.key, v)} /> : <input {...input} onChange={(e) => set(f.key, e.target.value)} />}
              </BulkRow>
            );
          })}
        </div>

        <div className="section-title">Tags</div>
        <div className="bulk-grid">
          <label className="bulk-label" htmlFor="bulk-add-tags">Add tags</label>
          <input id="bulk-add-tags" className="input" placeholder="tag, another tag" value={addTags} onChange={(e) => setAddTags(e.target.value)} />
          {tagCounts.length > 0 && (
            <>
              <span className="bulk-label" id="bulk-remove-tags">Remove tags</span>
              <div className="tag-toggles" role="group" aria-labelledby="bulk-remove-tags">
                {tagCounts.map(([t, n]) => (
                  <button key={t} type="button" className="tag-toggle" aria-pressed={removeTags.includes(t)} onClick={() => setRemoveTags((r) => (r.includes(t) ? r.filter((x) => x !== t) : [...r, t]))} title={`${plural(n, 'person', 'people')} here ${n === 1 ? 'has' : 'have'} this tag`}>
                    {t} <span className="faint">{n}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>

        {groups.length > 0 && (
          <>
            <div className="section-title">Groups</div>
            <div className="bulk-grid">
              <label className="bulk-label" htmlFor="bulk-join">Add to group</label>
              <div className="row wrap" style={{ gap: 6 }}>
                <select id="bulk-join" className="select" style={{ flex: '1 1 160px', width: 'auto' }} value={joinGroup} onChange={(e) => setJoinGroup(e.target.value)}>
                  <option value="">Don’t add</option>
                  {groups.map((g) => (
                    <option key={g.id} value={g.id} disabled={g.id === leaveGroup}>
                      {g.name}
                      {inGroup(g.id) ? ` (${inGroup(g.id) === people.length ? 'all' : inGroup(g.id)} already in it)` : ''}
                    </option>
                  ))}
                </select>
                {joinGroup && <input className="input" style={{ flex: '1 1 140px', width: 'auto' }} aria-label="Role in the group" placeholder="Role (optional)" value={role} onChange={(e) => setRole(e.target.value)} />}
              </div>
              {groups.some((g) => inGroup(g.id)) && (
                <>
                  <label className="bulk-label" htmlFor="bulk-leave">Remove from group</label>
                  <select id="bulk-leave" className="select" value={leaveGroup} onChange={(e) => setLeaveGroup(e.target.value)}>
                    <option value="">Don’t remove</option>
                    {groups
                      .filter((g) => inGroup(g.id))
                      .map((g) => (
                        <option key={g.id} value={g.id} disabled={g.id === joinGroup}>
                          {g.name} ({inGroup(g.id) === people.length ? 'all' : `${inGroup(g.id)} of them`})
                        </option>
                      ))}
                  </select>
                </>
              )}
            </div>
          </>
        )}

        <div className="section-title">Notes</div>
        <div className="field">
          <textarea className="textarea" rows={3} aria-label="Add to notes" placeholder="Added to the end of each person’s notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

/** A field that only changes when ticked; using its control ticks it. */
function BulkRow({ label, on, onToggle, hint, autoFocus, children }: { label: string; on: boolean; onToggle: () => void; hint?: string; autoFocus?: boolean; children: ReactNode }) {
  return (
    <>
      <label className="check bulk-label">
        <input type="checkbox" checked={on} onChange={onToggle} autoFocus={autoFocus} aria-label={`Change ${label.toLowerCase()}`} />
        {label}
      </label>
      <div className={`bulk-control${on ? '' : ' off'}`}>
        {children}
        {hint && <div className="hint">{hint}</div>}
      </div>
    </>
  );
}
